import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, effect, ElementRef, inject, signal, ViewChild } from '@angular/core';
import { RegionsInfo, SitesShortForm } from '../../interfaces/regions';
import { LoadProjectionsRecord } from '../../interfaces/load-projections-record';
import { CommonModule } from '@angular/common';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { LookUpService } from '../../services/lookupservice';
import { MatCardModule } from '@angular/material/card';
import { FeedstockService } from '../../services/feedstockservice';
import { NotificationService } from '../../services/notificationservice';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { Subject, takeUntil } from 'rxjs';
import { DosingService } from '../../services/dosing.service';
import { NutritionalFact, DosingRates, ColumnMeta } from '../../models/dosing.model';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { Chart, registerables } from 'chart.js';
import { LegendItem } from 'chart.js';
import { ChartDataset } from 'chart.js';
import { AddeditDosingrateComponent } from './addedit-dosingrate/addedit-dosingrate.component';
import { MatDialog } from '@angular/material/dialog';
// CDK & Third Party
import { ScrollingModule } from '@angular/cdk/scrolling';
import { TableVirtualScrollModule, TableVirtualScrollDataSource } from 'ng-table-virtual-scroll';
import { RolePermissionsService } from '../../services/role-permissions.service';
import { LoadScheduleControlFlags, ScreenName } from '../../interfaces/securityPermissions';
import { RoleService } from '../../services/role.service';

Chart.register(...registerables);

@Component({
  selector: 'app-dosingrates',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatCheckboxModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    MatCardModule,
    MatSortModule,
    ScrollingModule, TableVirtualScrollModule
  ],

  templateUrl: './dosingrates.component.html',
  styleUrl: './dosingrates.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DosingratesComponent {
  // Dropdown state
  permissions = inject(RolePermissionsService);
  markets = signal<RegionsInfo[]>([]);
  selectedMarketId = signal<number | null>(-1);
  marketQuery = signal<string>('');
  manure = signal<number>(0);
  firstRecord = signal<DosingRates>({} as DosingRates);
  cdr = inject(ChangeDetectorRef);

  projectedRows = signal<any[]>([]);
  disposalSites = signal<SitesShortForm[]>([]);
  selectedSiteId = signal<number | null>(null);
  siteQuery = signal<string>('');
  private destroy$ = new Subject<void>();

  nutritionalFacts = signal<NutritionalFact[]>([]);

  dosingColumns: ColumnMeta[] = [];
  projectedColumns: ColumnMeta[] = [];

  displayedDosingColumns: string[] = [];
  displayedProjectedColumns: string[] = [];

  dataSourceDosing = new MatTableDataSource<any>([]);
  dataSourceProjected = new MatTableDataSource<any>([]);


  @ViewChild('marketFilterInput') marketFilterInput?: ElementRef<HTMLInputElement>;
  @ViewChild('siteFilterInput') siteFilterInput?: ElementRef<HTMLInputElement>;
  @ViewChild(MatSort) sort!: MatSort;

  @ViewChild('projectedVolumeChart')
  projectedVolumeChart!: ElementRef<HTMLCanvasElement>;

  private projectedChart!: Chart;
  @ViewChild('fullscreenProjectedVolumeChart')
  fullscreenProjectedVolumeChart!: ElementRef<HTMLCanvasElement>;

  private normalChart!: Chart;
  private fullscreenChart!: Chart;

  private cachedChartData: any[] = [];
  dosingColorMap: Record<string, string> = {};
  private cachedColNames: string[] = [];

  isDuplicateDisabled = computed(() => {
    const siteIdNull = this.selectedSiteId() === null;
    const recordEmpty = !this.firstRecord() || Object.keys(this.firstRecord()).length === 0;
    
    // Logic: Disabled if site is null OR if there's no record to duplicate
    return siteIdNull && recordEmpty;
  });
  controlPermissions: LoadScheduleControlFlags = new LoadScheduleControlFlags();
  ngAfterViewInit(): void {
    // Render empty chart first so the canvas is ready
    this.renderEmptyProjectedChart();
  }

  constructor(private _lookupService: LookUpService,
    private _fsService: FeedstockService, private _notificationService: NotificationService,
    private _dosingService: DosingService, private dialog: MatDialog, private rolesService: RoleService) {

    this.loadMarkets();

    //When market changes: reset site, load sites, and clear pending
    effect(() => {
      const marketId = this.selectedMarketId();
      const siteId = this.selectedSiteId();
      this.loadDisposalSites(marketId!);
      this.loadNutritionalFacts(siteId ?? 0);
      this.loadDosingRates(siteId ?? 0);
      this.loadSiteDailyDosingVolumes(siteId ?? 0);
    });
  }

  isProjectedVolumeMaximized = signal(false);

  private renderEmptyProjectedChart(): void {
    if (!this.projectedVolumeChart) return;

    this.normalChart?.destroy();
    this.normalChart = new Chart(this.projectedVolumeChart.nativeElement, {
      type: 'line',
      data: {
        labels: [], // empty state
        datasets: [
          {
            label: 'Projected Volume (no data)',
            data: [],
            borderColor: '#bdbdbd',
            borderDash: [6, 4],
            tension: 0.3,
            pointRadius: 0
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: true } },
        scales: {
          y: { beginAtZero: true }
        }
      }
    });
  }

  private renderProjectedVolumeWhenReady(): void {
    requestAnimationFrame(() => {
      if (this.projectedVolumeChart?.nativeElement) {
        this.renderNormalChart();
      } else {
        setTimeout(() => this.renderProjectedVolumeWhenReady(), 50);
      }
    });
  }  

  private loadMarkets(): void {
    this._lookupService.getMarketsLookupForProjection().pipe(takeUntil(this.destroy$)).subscribe({
      next: (regions: RegionsInfo[]) => {
        this.markets.set(regions);
      }
    })
  }

  private loadDisposalSites(marketId: number): void {
    this._lookupService.getSitesByMarketId(marketId, 1).pipe(takeUntil(this.destroy$)).subscribe({
      next: (sites: SitesShortForm[]) => {
        this.disposalSites.set(sites);
      }
    })
    
    let roles = this.rolesService.getRolesAsString();
    this.permissions.getRolePermissionsNew(roles, ScreenName.DosingRates, 0).subscribe({
      next: (perms: LoadScheduleControlFlags) => {
        this.controlPermissions = perms;
      }
    })
  }

  private loadNutritionalFacts(siteId: number): void {
    this._dosingService.getNutritionalFacts(siteId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (nutres: NutritionalFact[]) => {
        this.nutritionalFacts.set(nutres);

      }
    })
  }

  private loadDosingRates(siteId: number): void {
    if(siteId === 0) {
      this.dosingColumns = [];
      this.displayedDosingColumns = [];
      this.dataSourceDosing.data = [];
      return;
    }
    this._dosingService.getDosingRatee(siteId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (dosingres: DosingRates) => {
        console.log('Dosing Rates Data:', dosingres);
        this.dosingColumns = dosingres.columnNames;
        this.dosingColumns.unshift({ key: 'action', header: 'Action', type: '', order: 0 });
        this.dosingColorMap = {};
        (dosingres.colorCodes ?? []).forEach((x: any) => {
          if (x.colorCode || x.ColorCode) {
            this.dosingColorMap[x.columnName ?? x.ColumnName] = x.colorCode ?? x.ColorCode;
          }
        });
        this.displayedDosingColumns = this.dosingColumns.map(c => c.key);
        
        this.dataSourceDosing.data = [...dosingres.data];

        this.manure.set(dosingres.data.length > 0 ? dosingres.data[0]['Manure_Dosing'] : 0);
        this.firstRecord.set(dosingres.data.length > 0 ? dosingres.data[0] : ({} as DosingRates));

        this.cdr.markForCheck();
      }
    })
  }

  getDosingCellStyle(colKey: string): { [key: string]: string } {
    const color = this.dosingColorMap[colKey];
  
    return color ? { 'background-color': color } : {};
  }

  private loadSiteDailyDosingVolumes(siteId: number): void {
    if(siteId === 0) {
      this.projectedColumns = [];
      this.displayedProjectedColumns = [];
      this.dataSourceProjected.data = [];
      return;
    }
    this._dosingService.getSiteDailyDosingVolumes(siteId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(res => {
        console.log('Projected Volume Data:', res);
        this.cachedChartData = res.data;
        this.projectedColumns = res.columnNames;
        this.projectedRows.set([...res.data]);
        this.displayedProjectedColumns = this.projectedColumns.map(c => c.key);
        this.dataSourceProjected.data = [...res.data];
        this.projectedRows.set([...res.data]); 
        if ((res.data?.length ?? 0) > 0) {
          this.renderProjectedVolumeWhenReady();
        } else {
          this.renderEmptyProjectedChart();
        }
      });
  }


  // private PopulateChartTable()  {
  //   console.log('Populating chart table with data:',  this.cachedColNames);
  // }
  get hasProjectedVolumeData(): boolean {
    return (this.cachedChartData?.length ?? 0) > 0;
  }

  openProjectedVolume(): void {
    this.isProjectedVolumeMaximized.set(true);
    document.body.style.overflow = 'hidden';

    setTimeout(() => this.renderFullscreenChart(), 0);
  }

  closeProjectedVolume(): void {
    this.isProjectedVolumeMaximized.set(false);
    document.body.style.overflow = '';

    this.fullscreenChart?.destroy();
  }

  // Filtered views (case-insensitive)
  marketsFiltered = computed(() => {
    const q = this.marketQuery().trim().toLowerCase();
    return this.markets().filter(m => m.regionName.toLowerCase().includes(q));
  });

  sitesFiltered = computed(() => {
    const q = this.siteQuery().trim().toLowerCase();
    return this.disposalSites().filter(s => s.siteName.toLowerCase().includes(q));
  });

  // --- Market select handlers
  onMarketSelected(value: number): void {
    this.selectedMarketId.set(value);
    this.selectedSiteId.set(null);
  }

  onMarketQuery(event: Event): void {
    const searchTerm = (event.target as HTMLInputElement).value.toLowerCase();
    this.marketQuery.set(searchTerm);
  }

  onMarketOpened(open: boolean): void {
    if (open) setTimeout(() => this.marketFilterInput?.nativeElement.focus(), 0);
  }

  // --- Site select handlers
  onSiteSelected(value: number | null): void {
    this.selectedSiteId.set(value);
  }

  onSiteQuery(event: Event): void {
    const searchTerm = (event.target as HTMLInputElement).value.toLowerCase();
    this.siteQuery.set(searchTerm);
  }

  onSiteOpened(open: boolean): void {
    if (open) setTimeout(() => this.siteFilterInput?.nativeElement.focus(), 0);
  }

  private renderProjectedVolumeChart(data: any[]): void {
    this.cachedChartData = data;

    this.renderNormalChart();
  }

  private renderNormalChart(): void {
    if (!this.projectedVolumeChart) return;

    this.normalChart?.destroy();
    this.normalChart = this.createChart(
      this.projectedVolumeChart.nativeElement
    );
  }

  private renderFullscreenChart(): void {
    if (!this.fullscreenProjectedVolumeChart) return;

    this.fullscreenChart?.destroy();
    this.fullscreenChart = this.createChart(
      this.fullscreenProjectedVolumeChart.nativeElement
    );
  }

  private createChart(canvas: HTMLCanvasElement): Chart {
   const data = this.cachedChartData;

   // X-axis labels → DATE ONLY
   const labels = data.map(d =>
     new Date(d.Datecaptured).toLocaleDateString('en-US', {
       month: 'short',
       day: '2-digit'
     })
   );

   const dynamicKeys = Object.keys(data[0]).filter(key =>
    key !== 'Datecaptured' && key !== 'SiteID'
  );

  const toLabel = (key: string) => key
    .replace(/_/g, ' ')
    .replace(/Volume/gi, '')
    .trim();

  const getPointStyle = (key: string) => {
    const k = key.toLowerCase();
    if (k.startsWith('green')) return 'circle';
    if (k.startsWith('yellow')) return 'rect';
    if (k.startsWith('red')) return 'triangle';
    if (k.startsWith('solids')) return 'diamond';
    return 'circle';
  };
    const predefinedColors: Record<string, string> = {
      Green: '#008000',
      Yellow: '#FFD700',
      Red: '#FF0000',
      Solids: '#B6E1EFD1'
    };

    const fallbackColors = [
      '#3366CC',
      '#DC3912',
      '#FF9900',
      '#109618',
      '#990099'
    ];

    const datasets = dynamicKeys.map((key, i) => {

      const seriesName = key.split('_')[0];

      const volumeColors: Record<string, string> = {
        Green: '#008000',
        Yellow: '#FFD700',
        Red: '#FF0000',
        Solids: '#4FC3F7',

        HT1: '#1976D2',
        HT2: '#388E3C',
        HT3: '#F57C00',
        PWT: '#7B1FA2'
      };

      const minColors: Record<string, string> = {
        Green: '#66BB6A',
        Yellow: '#FFF176',
        Red: '#EF9A9A',
        Solids: '#B3E5FC',

        HT1: '#90CAF9',
        HT2: '#A5D6A7',
        HT3: '#FFCC80',
        PWT: '#CE93D8'
      };

      const maxColors: Record<string, string> = {
        Green: '#2E7D32',
        Yellow: '#F9A825',
        Red: '#C62828',
        Solids: '#0288D1',

        HT1: '#1565C0',
        HT2: '#2E7D32',
        HT3: '#EF6C00',
        PWT: '#6A1B9A'
      };

      const opMaxColors: Record<string, string> = {
        Green: '#1B5E20',
        Yellow: '#F57F17',
        Red: '#8E0000',
        Solids: '#01579B',

        HT1: '#0D47A1',
        HT2: '#1B5E20',
        HT3: '#E65100',
        PWT: '#4A148C'
      };

      let color: string;

      if (key.endsWith('_Volume')) {
        color = volumeColors[seriesName];
      }
      else if (key.endsWith('_VolumeMin')) {
        color = minColors[seriesName];
      }
      else if (key.endsWith('_VolumeMax')) {
        color = maxColors[seriesName];
      }
      else if (key.endsWith('_OpMax')) {
        color = opMaxColors[seriesName];
      }
      else {
        color = fallbackColors[i % fallbackColors.length];
      }

      const isActualVolume = key.endsWith('_Volume');

      return {
        type: 'line',
        label: toLabel(key),
        data: data.map(d => Number(d[key] ?? 0)),

        borderColor: color,
        backgroundColor: color,

        borderWidth: 2,

        pointRadius: isActualVolume ? 4 : 0,
        pointHoverRadius: isActualVolume ? 4 : 0,

        pointStyle: getPointStyle(key),

        tension: 0,
        spanGaps: false,

        order: isActualVolume ? 1 : 0
      } as ChartDataset<'line'>;
    });

    return new Chart(canvas, {
      type: 'line',
      data: { labels, datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'nearest', intersect: false },
        plugins: {
          legend: {
            position: 'bottom',
            labels: {
              boxWidth: 40,
              boxHeight: 2,
              usePointStyle: false,
              generateLabels: (chart) =>
                chart.data.datasets.map((ds, i) => ({
                  text: ds.label ?? '',
                  strokeStyle: ds.borderColor as string,
                  fillStyle: ds.borderColor as string,
                  lineWidth: (ds.borderWidth ?? 2),
                  hidden: !chart.isDatasetVisible(i),
                  datasetIndex: i
                })) as any
            },
            onClick: (e, item, legend) => {
              const ci = legend.chart;
              const index = item.datasetIndex ?? 0;
              ci.setDatasetVisibility(index, !ci.isDatasetVisible(index));
              ci.update();
            }
          },
          tooltip: {
            callbacks: {
              title: (items) => {
                const idx = items[0].dataIndex;
                return new Date(data[idx].Datecaptured).toLocaleString('en-US', {
                  month: 'short',
                  day: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: true
                });
              },
              label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y?.toLocaleString()}`
            }
          }
        },
        scales: {
          x: {
            title: { display: true, text: 'Date' },
            ticks: { maxRotation: 0, autoSkip: true }
          },
          y: {
            title: { display: true, text: 'Volume' },
            beginAtZero: true,
            ticks: { callback: (v) => v.toLocaleString() }
          }
        }
      }
    });
  }

  fullscreenCard = signal<string | null>(null);

  openFullscreen(card: string) {
    this.fullscreenCard.set(card);
    document.body.style.overflow = 'hidden';
  }

  closeFullscreen() {
    this.fullscreenCard.set(null);
    document.body.style.overflow = '';
  }
  // --- trackBy helpers
  trackRow = (_: number, row: LoadProjectionsRecord) => row.id;
  trackByMarketId = (_: number, item: RegionsInfo) => item.id;
  trackBySiteId = (_: number, item: SitesShortForm) => item.id;
  
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  openAddEditForm(row: any, mode: 'Add' | 'Edit' | 'Duplicate'): void {
    console.log("Editing record", row);
    if(mode === 'Duplicate'){
      row = this.firstRecord();
    }
    const dialogRef = this.dialog.open(AddeditDosingrateComponent, {
      disableClose: true,
      width:'650px',
      maxWidth:'750px',
      data: {
        siteId: this.selectedSiteId()!,
        siteName: this.disposalSites().find(s => s.id === this.selectedSiteId())?.siteName || '',
        isEditMode: mode === 'Edit',
        inputDate: (row && mode === 'Edit') ? new Date(row.Datecaptured) : null,
        existingData: row || ({} as DosingRates),
        manureToSet: mode === "Edit" ? row["Manure_Dosing"] :this.manure(),
        task: mode,
        canSave: this.controlPermissions.edit || this.controlPermissions.addButton
      }
    });

    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result) {
        this.loadDosingRates(this.selectedSiteId() ?? 0);
      }
    });
  }
}
